import { aiSignal } from "@shared/libs/aiSignal";
import axios from "axios";
import { showDialog } from "@widgets/CommonDialog";
import {
  footerMsgStore,
  globalMenu,
  requestControllerStore,
  loadingTypeStore,
  global,
} from "@shared/libs/Global";
import { useNavigationStore } from "@shared/libs/Global";
import { apiFetch } from "@shared/api/api";

const baseURL = process.env.REACT_APP_API_BASE_URL;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * QUERY OR PROCEDURE 호출 API 메서드
 * @param endpoint URL
 * @param params 실행 시 필요한 변수
 */
export const callApi = async (endpoint: string, params: any): Promise<any> => {
  const activeMenuInfo = globalMenu.getState().activeMenuInfo;
  const handleFooterMsg = footerMsgStore.getState().handleFooterMsg;
  let message = "";

  try {
    const response = await axios.post("/snotes/" + endpoint, params, {
      headers: {
        "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
      },
    });
    const result = response.data.result;

    const if_status = result?.if_status || "";
    if (if_status === "E") {
      // 오류
      const temp = `${result.code ? "[" + result.code + "] " : ""}${result.message}`;
      message = getMessage(result.type, temp);
      switch (result.type) {
        case "alter":
          await showDialog("alter", message);
          break;
        case "error":
          await showDialog("error", message);
          break;
        default:
          await showDialog("error", message);
          break;
      }
      return { status: if_status === "S", data: result.return_item };
    }
    const data = result.return_item || {};
    const type = result.type || "";
    const temp = result.message || "";
    if (type && temp) {
      await showDialog(type, temp);
    }
    message = getMessage(type, temp);
    return { status: if_status === "S", data: data };
  } catch (error: any) {
    if (error.response?.statusText === "Unauthorized") {
      // 인증 만료로 로그인 화면에 보낼 때도 테넌트를 유지한다.
      // DEFAULT는 붙이지 않는다 — 로그인 화면이 ?tenant=DEFAULT를 모르는 테넌트로
      // 오탐해서 파라미터를 떼며 전체 리로드가 한 번 더 발생한다.
      const tenantId = global.getState().userInfo.tenant_id || "DEFAULT";
      const loginPath =
        tenantId === "DEFAULT" ? "/login" : "/login?tenant=" + tenantId;
      const navigate = useNavigationStore.getState().navigate;
      if (navigate) {
        navigate(loginPath);
      } else {
        // Fallback
        window.location.href = loginPath;
      }
    } else {
      await showDialog(
        "error",
        "API 호출 중 오류가 발생했습니다. \n\n" + error.message
      );
      return {
        status: false,
        data: null,
      };
    }
  } finally {
    if (activeMenuInfo.cd_menu)
      handleFooterMsg(activeMenuInfo.cd_menu, message);
  }
};

/**
 * SELECT API 호출 메서드
 * @param params id : cd_query / params : 쿼리 파라미터
 */
export const callApiRes = async (
  endpoint: string,
  params: any
): Promise<any> => {
  const activeMenuInfo = globalMenu.getState().activeMenuInfo;
  const menuId = activeMenuInfo.cd_menu;
  const { abortController, setController, removeController } =
    requestControllerStore.getState();
  const controller = new AbortController();
  setController(menuId, controller);
  try {
    const response = await axios.post("/snotes/" + endpoint, params, {
      signal: controller.signal,
      headers: {
        "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
      },
    });

    const result = response.data.result;
    const if_status = result?.if_status || "";
    // 사람이 조회했다. v2 답 뒤라면 조건을 고쳤는지 본다(값은 안 보낸다). 실패는 삼킨다
    if (if_status !== "E") {
      try {
        aiSignal.onManualSearch(menuId, params);
      } catch {
        /* 수집용 부가 기능 */
      }
    }
    if (if_status === "E") {
      // 오류
      const temp = `${result.code ? "[" + result.code + "] " : ""}${result.message}`;
      const message = getMessage(result.type, temp);
      switch (result.type) {
        case "alter":
          await showDialog("alter", message);
          break;
        case "error":
          await showDialog("error", message);
          break;
        default:
          await showDialog("error", message);
          break;
      }
      return;
    }

    return response.data; // JSON 형태로 응답 반환
  } catch (error: any) {
    if (axios.isCancel(error) || error.name === "CanceledError") {
      console.warn(`[${menuId}] 요청 취소됨`);
      return { status: "cancelled", data: null };
    }

    console.error(`[${menuId}] API 실패`, error);
    throw error;
  } finally {
    loadingTypeStore.getState().clearLoadingType(menuId);
    removeController(menuId); // 완료 시 정리
  }
};

/**
 * SELECT API 호출 메서드
 * @param params id : cd_query / params : 쿼리 파라미터
 * @param options silentError : 조회 실패를 오류창으로 알리지 않는다.
 *                코드 존재 확인처럼 "못 찾는 것도 정상"인 조회에서 쓴다.
 */
export const fnGetDataSet = async (
  params: any,
  options?: { silentError?: boolean }
) => {
  const activeMenuInfo = globalMenu.getState().activeMenuInfo;
  const menuId = activeMenuInfo.cd_menu;

  const { abortController, setController, removeController } =
    requestControllerStore.getState();

  loadingTypeStore.getState().setLoadingType(menuId, "search");

  // 중복 요청 중단
  // abortController(menuId); // 우클릭메뉴가 비활성화되서 주석처리함 (25.07.02)

  const controller = new AbortController();
  setController(menuId, controller);

  try {
    const response = await axios.post("/snotes/getDataSet", params, {
      signal: controller.signal,
      headers: {
        "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
      },
    });

    const result = response.data.result;
    const if_status = result?.if_status || "";
    if (if_status === "E") {
      // 오류
      const temp = `${result.code ? "[" + result.code + "] " : ""}${result.message}`;
      const message = getMessage(result.type, temp);
      if (options?.silentError) {
        console.warn(`[${menuId}] 조회 실패(무시)`, message);
        return;
      }
      switch (result.type) {
        case "alter":
          await showDialog("alter", message);
          break;
        case "error":
          await showDialog("error", message);
          break;
        default:
          await showDialog("error", message);
          break;
      }
      return;
    }

    return response.data; // JSON 형태로 응답 반환
  } catch (error: any) {
    if (axios.isCancel(error) || error.name === "CanceledError") {
      console.warn(`[${menuId}] 요청 취소됨`);
      return { status: "cancelled", data: null };
    }

    console.error(`[${menuId}] API 실패`, error);
    throw error;
  } finally {
    loadingTypeStore.getState().clearLoadingType(menuId);
    removeController(menuId); // 완료 시 정리
  }
};

export const fnLogin = async (params: any) => {
  try {
    // debugger;
    const response = await axios.post("/snotes/login", params, {
      withCredentials: true,
      headers: {
        "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
      },
    });

    const contentType = response.headers["content-type"];
    const isJson = contentType && contentType.includes("application/json");
    if (!isJson) {
      await showDialog("alter", "아이디 또는 비밀번호가 잘못되었습니다.");
      return;
    }

    if (response.data?.result?.if_status === "E") {
      response.data.result!.message = "계정 정보를 확인해 주세요.";
    }

    return response.data; // JSON 형태로 응답 반환
  } catch (error: any) {
    console.error("로그인 실패", error);

    const response = error.response?.data;
    const result = response?.result;

    let message = "로그인에 실패했습니다. 관리자에게 문의하세요.";
    if (result) {
      message = result.message;
    }
    throw new Error(message);
  }
};

export const fnLoginCheck = async () => {
  // if (window.location.pathname !== '/login') {
  const response = await apiFetch("/loginCheck");
  // if (!response.authenticated) throw new Error('not authenticated');
  // return response.authenticated;
  // }
  /*
  try {
    const response = await axios.get("/snotes/loginCheck", {
      withCredentials: true,
    });
    // return response.data; // JSON 형태로 응답 반환
    return response.data.authenticated; // JSON 형태로 응답 반환
  } catch (error) {
    console.error("API 호출 실패1", error);
    throw error;
  }
   */
};

export const fnLogout = async () => {
  try {
    const response = await axios.post(
      "/snotes/logout",
      {},
      {
        withCredentials: true,
        headers: {
          "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
        },
      }
    );
    console.log(response);
    // return response.data; // JSON 형태로 응답 반환
    return response.data; // JSON 형태로 응답 반환
  } catch (error) {
    console.error("API 호출 실패1", error);
    throw error;
  }
};

/**
 * INSERT, UPDATE, DELETE API 호출 메서드
 * @param endpoint URL
 * @param params 변경할 데이터
 */
export const fnSave = async (
  endpoint: string,
  params: any,
  msg?: string,
  options?: { silentSuccess?: boolean }
) => {
  const activeMenuInfo = globalMenu.getState().activeMenuInfo;
  const handleFooterMsg = footerMsgStore.getState().handleFooterMsg;
  let message = "";
  let temp = "";

  try {
    const response = await axios.post("/snotes/" + endpoint, params, {
      headers: {
        "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
      },
    });
    const result = response.data.result;
    const if_status = result?.if_status || "";
    if (if_status === "E") {
      // 오류
      const temp = `${result.code ? "[" + result.code + "] " : ""}${result.message}`;
      message = getMessage(result.type, temp);
      switch (result.type) {
        case "alter":
          await showDialog("alter", message);
          break;
        case "error":
          await showDialog("error", message);
          break;
        default:
          await showDialog("error", message);
          break;
      }
      return false;
    }

    switch (msg) {
      case "APPROVE":
        temp = "승인 처리되었습니다.";
        break;
      case "APPROVE_CANCEL":
        temp = "승인 취소되었습니다.";
        break;
      case "CLOSE":
        temp = "마감 처리되었습니다.";
        break;
      case "CLOSE_CANCEL":
        temp = "마감 취소되었습니다.";
        break;
      default:
        temp = "저장이 완료되었습니다.";
        break;
    }
    if (!options?.silentSuccess) {
      await showDialog("success", temp);
    }
    message = getMessage("success", temp);
    return result.return_item || true;
  } catch (error: any) {
    // const data = error.response.data;
    // const temp = `${data.code ? "[" + data.code + "] " : ""}${data.message}`;
    // message = getMessage(data.type, temp);
    // switch (data.type) {
    //   case "alter":
    //     await showDialog("alter", message);
    //     break;
    //   case "error":
    //     await showDialog("error", message);
    //     break;
    //   default:
    //     await showDialog("error", message);
    //     break;
    // }
    throw { error: error, message: message };
  } finally {
    if (activeMenuInfo.cd_menu)
      handleFooterMsg(activeMenuInfo.cd_menu, message);
  }
};

/**
 * type에 맞춰서 message 만들어주는 공통 메서드
 * @param type dialog type(success, alter, error)
 * @param message 내용
 */
const getMessage = (type: string, message: string) => {
  let returnMsg: string = message;
  switch (type) {
    case "success":
      returnMsg = "[성공] " + returnMsg;
      break;
    case "alter":
      returnMsg = "[경고] " + returnMsg;
      break;
    case "error":
      returnMsg = "[오류] " + returnMsg;
      break;
  }
  return returnMsg;
};

// 라이선스 정보 조회 API
export const fnGetLicenseInfo = async (): Promise<any | null> => {
  try {
    const response = await axios.get("/snotes/admin/license/info", {
      withCredentials: true,
      headers: {
        "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
      },
    });
    return response.data;
  } catch (error) {
    console.error("라이선스 정보 조회 실패:", error);
    return null;
  }
};

// 라이선스 업로드 API
// licenseJson: 라이선스 JSON 파일
// licenseSig: 라이선스 서명 파일
// publicKey: 공개 키 파일 (추가된 부분)
// 반환값: 성공 시 "라이선스 업로드 완료" 메시지, 실패 시 에러 메시지
// 주의: publicKey 파일은 선택 사항이며, 필요 시에만 전달합니다.
//       만약 publicKey가 필요하지 않다면, 해당 파라미터를 생략할 수 있습니다.
export const fnUploadLicense = async (
  licenseJson: File,
  licenseSig: File,
  publicKey: File
): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append("licenseJson", licenseJson);
    formData.append("licenseSig", licenseSig);
    formData.append("publicKey", publicKey);
    const response = await axios.post(
      "/snotes/admin/license/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
        },
        withCredentials: true,
      }
    );

    return response.data || "라이선스 업로드 완료";
  } catch (error: any) {
    const msg =
      error.response?.data?.message ||
      error.response?.data ||
      error.message ||
      "업로드 중 알 수 없는 오류 발생";

    return "❌ " + msg;
  }
};

export const fnGetTableById = async (params: { id: string; params: any }) => {
  try {
    const response = await axios.post("/snotes/getTableById", params, {
      headers: {
        "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
      },
    });
    return response.data;
  } catch (error) {
    console.error("API 호출 실패1", error);
    throw error;
  }
};
